import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { inject, makeEnvironmentProviders } from '@angular/core';

import { ConfigurationService, Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { ApiDiscoveryEntry, Protocol } from '../../internal/http/service-discovery/api-discovery.types';
import {
    SERVICE_DISCOVERY_FINDER,
    ServiceDefinitionFinder,
    matchesServicePath,
    stripStartSlash
} from '../../internal/http/service-discovery/service-discovery.service';
import { RwcDevToolFeature, RwcDevToolFeatureKind, rwcDevToolFeature } from '../rwc-dev-tools.providers';

import { localBackendInterceptor } from './local-backend.interceptor';
import { LocalBackend } from './local-backend.types';

/**
 * Provides local backends for development stage. It allows to route requests to specified local backends instead of real services.
 * It should only be used in 'local-dev' stage and will throw an error if used in any other stage. Use isDevMode() to conditionally provide it.
 *
 * @example
 *
 *  provideRwcAzureDevTools(withLocalBackend([
 *      {
 *        path: 'domain/service',
 *        baseUrl: 'http://localhost:8080'
 *        headers : {
 *            // If not overridden like here, per default the x-identity is the current (impersonated) userId
 *            'x-identity': buildIdentityHeader({ sub: 'U123456' }),
 *            groups: ['FNL_Administrator_Fuehrungsprozess', 'FNL_Mitarbeiter_Fuehrungsprozess'],
 *            // example for on prem services:
 *            x-caller: RwcConfig.getTkId(),
 *            x-user-id: 'U123456',
 *        }
 *      }
 *  ]))
 */
export function withLocalBackend(services: LocalBackend[]): RwcDevToolFeature<RwcDevToolFeatureKind.RwcLocalBackend> {
    const providers = services.length
        ? [
              { provide: SERVICE_DISCOVERY_FINDER, useFactory: getServiceDefinitionFactory(services) },
              provideHttpClient(withInterceptors(localBackendInterceptor(services)))
          ]
        : [];
    return rwcDevToolFeature(RwcDevToolFeatureKind.RwcLocalBackend, [makeEnvironmentProviders(providers)]);
}

const getServiceDefinitionFactory =
    (services: LocalBackend[]): (() => ServiceDefinitionFinder) =>
    () => {
        const stage = inject(ConfigurationService).getSync('stage');
        if (stage !== 'local-dev') {
            throw new Error(
                `withLocalBackend can only be used in 'local-dev' stage but is '${stage}'. Use isDevMode() to conditionally provide it.`
            );
        }
        services.forEach(b => validateLocalBackendPath(b.path));
        const logger = inject(Logger).createCustomLogger('LocalBackend');
        logger.info(`Uses local backend for ${services.map(backend => backend.path).join(', ')}`);
        const localBackends = toApiDiscoveryEntry(services);

        return ((relativePath, serviceDefinitions) => {
            const allServiceDefinitions: ApiDiscoveryEntry[] = [...localBackends, ...serviceDefinitions.values()];
            return getServiceDefinition(relativePath, allServiceDefinitions);
        }) satisfies ServiceDefinitionFinder;
    };

function validateLocalBackendPath(path: string): void {
    if (path.startsWith('/')) {
        throw new Error(`Invalid local backend path '${path}': must not start with '/'.`);
    }
    if (path.endsWith('/')) {
        throw new Error(`Invalid local backend path '${path}': must not end with '/'.`);
    }
    if (path.includes('*')) {
        throw new Error(`Invalid local backend path '${path}': wildcard segments ('*', '**') are not supported.`);
    }
}

function toApiDiscoveryEntry(localBackends: LocalBackend[]): ApiDiscoveryEntry[] {
    return localBackends.map(localBackend => {
        const parsedBaseUrl = new URL(localBackend.baseUrl);
        const protocol = parsedBaseUrl.protocol === 'https' ? Protocol.HTTPS : Protocol.HTTP;
        const normalizedPath = `${parsedBaseUrl.host}${parsedBaseUrl.pathname}`.replace(/\/$/, '');

        return {
            host: normalizedPath,
            path: stripStartSlash(localBackend.path) as ApiDiscoveryEntry['path'],
            protocols: [protocol]
        };
    });
}

export function getServiceDefinition<T extends { path: string }>(relativePath: string, serviceDefinitions: T[]): T {
    const definition = serviceDefinitions.find(definition => matchesServicePath(relativePath, definition.path));
    if (!definition) {
        throw new Error(`No service definition found for path ${relativePath}`);
    }
    return definition;
}
